import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../api/axiosConfig";
import "./UserLogin.css";
import PasswordInput from "./PasswordInput";
import {
  FiUser,
  FiLock,
  FiMail,
  FiKey,
  FiLogIn,
  FiChevronDown,
} from "react-icons/fi";
import { FaGoogle } from "react-icons/fa";

// Custom Dropdown Component for full styling control
const CustomDropdown = ({ options, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleOptionClick = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="custom-dropdown-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className="custom-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption ? selectedOption.label : "Select Role"}</span>
        <FiChevronDown className={`chevron-icon ${isOpen ? "open" : ""}`} />
      </button>
      {isOpen && (
        <ul className="custom-dropdown-panel">
          {options.map((option) => (
            <li
              key={option.value}
              className={`custom-dropdown-option ${
                value === option.value ? "selected" : ""
              }`}
              onClick={() => handleOptionClick(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UserLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("director");
  const [error, setError] = useState("");
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showGmailOTP, setShowGmailOTP] = useState(false);
  const [gmailOtp, setGmailOtp] = useState("");
  const [gmailOtpError, setGmailOtpError] = useState("");
  const [emailForGmailOTP, setEmailForGmailOTP] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  // Check for session expiry message on component mount
  useEffect(() => {
    const expiredMessage = localStorage.getItem('sessionExpiredMessage');
    if (expiredMessage) {
      setSessionExpiredMessage(expiredMessage);
      localStorage.removeItem('sessionExpiredMessage'); // Clear it after showing
    }
  }, []);

  const roleOptions = [
    { value: "director", label: "Director" },
    { value: "zonal_head", label: "Zonal Head" },
    { value: "branch_manager", label: "Branch Manager" },
    { value: "team_lead", label: "Team Lead" },
    { value: "relationship_manager", label: "Relationship Manager" },
  ];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token) {
      const roleParam = urlParams.get("role");
      const nameParam = urlParams.get("name");
      // Set user token (allow concurrent logins)
      localStorage.setItem("userToken", token);
      localStorage.setItem("userRole", roleParam);
      localStorage.setItem("name", nameParam);
      window.history.replaceState({}, document.title, window.location.pathname);
      if (roleParam === "admin") {
        // Admin should not login through user login, redirect to admin login
        localStorage.removeItem("userToken");
        localStorage.removeItem("userRole");
        localStorage.removeItem("name");
        navigate("/admin-login");
      } else if (roleParam === "director") navigate("/director-dashboard");
      else if (roleParam === "zonal_head") navigate("/zonal-head-dashboard");
      else if (roleParam === "branch_manager")
        navigate("/branch-manager-dashboard");
      else if (roleParam === "team_lead") navigate("/team-lead-dashboard");
      else if (roleParam === "relationship_manager")
        navigate("/relationship-manager-dashboard");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await api.post("/api/auth/login", {
        username,
        password,
      });
      // Set user token (allow concurrent logins)
      localStorage.setItem("userToken", response.data.token);
      localStorage.setItem("userRole", response.data.role);
      localStorage.setItem("name", response.data.name);

      if (response.data.requiresPasswordChange) {
        navigate("/change-password");
      } else {
        const userRole = response.data.role;
        if (userRole === "admin") {
          setError("Admin users must login through the Admin Login page.");
          // Clear the token since admin shouldn't login here
          localStorage.removeItem("userToken");
          localStorage.removeItem("userRole");
          localStorage.removeItem("name");
        } else if (userRole === "director") navigate("/director-dashboard");
        else if (userRole === "zonal_head") navigate("/zonal-head-dashboard");
        else if (userRole === "branch_manager")
          navigate("/branch-manager-dashboard");
        else if (userRole === "team_lead") navigate("/team-lead-dashboard");
        else if (userRole === "relationship_manager")
          navigate("/relationship-manager-dashboard");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Invalid credentials or server error."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!role) {
      setError("Please select a role first.");
      return;
    }
    setShowEmailInput(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setEmailError("");
    if (!email) {
      setEmailError("Please enter a valid email address.");
      setIsLoading(false);
      return;
    }
    try {
      const response = await api.post("/api/auth/send-gmail-otp", {
        email,
      });
      // If we get here without an error, OTP was sent successfully
      setEmailForGmailOTP(email);
      setShowGmailOTP(true);
      setShowEmailInput(false);
    } catch (err) {
      setEmailError(
        err.response?.data?.message || "Server error. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyGmailOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setGmailOtpError("");
    try {
      const response = await api.post("/api/auth/verify-gmail-otp", {
        otp: gmailOtp,
        email: emailForGmailOTP,
      });
      // Set user token (allow concurrent logins)
      localStorage.setItem("userToken", response.data.token);
      localStorage.setItem("userRole", response.data.role);
      localStorage.setItem(
        "name",
        response.data.name || emailForGmailOTP.split("@")[0]
      );
      setShowGmailOTP(false);
      const userRole = response.data.role;
      if (userRole === "admin") {
        setGmailOtpError("Admin users must login through the Admin Login page.");
        // Clear the token since admin shouldn't login here
        localStorage.removeItem("userToken");
        localStorage.removeItem("userRole");
        localStorage.removeItem("name");
      } else if (userRole === "director") navigate("/director-dashboard");
      else if (userRole === "zonal_head") navigate("/zonal-head-dashboard");
      else if (userRole === "branch_manager")
        navigate("/branch-manager-dashboard");
      else if (userRole === "team_lead") navigate("/team-lead-dashboard");
      else if (userRole === "relationship_manager")
        navigate("/relationship-manager-dashboard");
    } catch (err) {
      setGmailOtpError(
        err.response?.data?.message || "OTP verification failed."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page-container">
      <div className="login-card">
        <div className="login-card-header">
          <h2>Login to Your Account</h2>
          <p>Select your role and authenticate to continue</p>
        </div>

        {sessionExpiredMessage && (
          <div className="alert alert-warning" style={{ 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            padding: '12px 16px', 
            borderRadius: '8px', 
            marginBottom: '16px',
            border: '1px solid #ffeeba',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '18px' }}>⚠️</span>
            {sessionExpiredMessage}
          </div>
        )}

        {!showEmailInput && !showGmailOTP && (
          <>
            <div className="input-group">
              <label>Select Your Role</label>
              <CustomDropdown
                options={roleOptions}
                value={role}
                onChange={setRole}
              />
            </div>

            <button
              className="btn btn-google"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <FaGoogle /> Login with Google
            </button>

            <div className="divider">OR</div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="input-group">
                <label>Username</label>
                <div className="input-wrapper">
                  <FiUser className="input-icon" />
                  <input
                    type="text"
                    className="form-control"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="input-group">
                <PasswordInput
                  label="Password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  error={error}
                  disableValidation={true}
                />
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                <FiLogIn /> {isLoading ? "Logging in..." : "Login"}
              </button>
            </form>
          </>
        )}

        {showEmailInput && (
          <div className="otp-form">
            <h4>Enter Your Gmail</h4>
            <form onSubmit={handleEmailSubmit}>
              <div className="input-group">
                <label>Gmail Address</label>
                <div className="input-wrapper">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              {emailError && (
                <div className="alert alert-danger">{emailError}</div>
              )}
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Send OTP"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEmailInput(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {showGmailOTP && (
          <div className="otp-form">
            <h4>Verify Gmail with OTP</h4>
            <form onSubmit={handleVerifyGmailOTP}>
              <div className="input-group">
                <label>Enter OTP sent to {emailForGmailOTP}</label>
                <div className="input-wrapper">
                  <FiKey className="input-icon" />
                  <input
                    type="text"
                    className="form-control"
                    value={gmailOtp}
                    onChange={(e) => setGmailOtp(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              {gmailOtpError && (
                <div className="alert alert-danger">{gmailOtpError}</div>
              )}
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={isLoading}
                >
                  {isLoading ? "Verifying..." : "Verify OTP"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowGmailOTP(false)}
                  disabled={isLoading}
                >
                  Back
                </button>
              </div>
            </form>
          </div>
        )}

        {!showEmailInput && !showGmailOTP && (
          <div className="footer-links-container">
            <div className="footer-links-row">
              <Link className="footer-link" to="/admin-login">
                Login as Admin
              </Link>
              <Link className="footer-link2" to="/user-forgot-password">
                Forgot Password?
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserLogin;
